import { router } from '@inertiajs/vue3'
import { uniqBy } from 'lodash-es'

import type { IndexBill } from '@/domains/bills/types/models/IndexBill.types'
import type { SearchScope, SearchScopeId } from '@/domains/command-center/services/searchService.types'
import type { MatchedVariant, SearchResultCommand, SkuMatchInfo } from '@/domains/command-center/types'
import type { Invoice } from '@/domains/invoices/types/models/Invoice.types'
import type { LoyaltyCustomer } from '@/domains/orders/types/models/LoyaltyCustomer.types'
import type { IndexOrder } from '@/domains/orders/types/responses/IndexOrder.types'
import type { IndexProduct } from '@/domains/products/types/models/IndexProduct.types'
import type { User } from '@/domains/users/types/models/User.types'

import { useSkuDisplay } from '@/composables/use-sku-display/useSkuDisplay'
import { useSkuMatcher } from '@/composables/use-sku-matcher/useSkuMatcher'
import { callBills } from '@/domains/bills/services'
import { callInvoices } from '@/domains/invoices/services'
import { measurementUnitsQueryOptions } from '@/domains/measurement-units/composables/use-measurement-units-query/useMeasurementUnitsQuery'
import { stockUnitOfRelation } from '@/domains/measurement-units/composables/use-measurement-units-query/useMeasurementUnitsQuery.utils'
import { callOrders } from '@/domains/orders/services'
import { useProductVariantHelper } from '@/domains/products/composables/use-product-variant-helper/useProductVariantHelper'
import { callProducts } from '@/domains/products/services'
import { SKU_FORMAT } from '@/domains/products/types/models/SkuCodeModel.types'
import { isPack } from '@/domains/products/utils/productComposition'
import { stockLevelBadge, stockSummary } from '@/domains/products/utils/stock-level/stockLevel'
import { queryClient } from '@/support/query/queryClient'
import { toTitleCase } from '@/utils/casing-util/casingUtil'

/**
 * Five rows per scope. Four scopes at five rows fills the palette without pushing the curated
 * actions off screen, and it is the one lever that keeps a fan-out of four listing endpoints
 * affordable — `BillDTO` carries its items, `OrderIndexDto` carries five relations.
 */
const ROWS_PER_SCOPE = 5

/**
 * The sidebar's own icon for each scope, so a pill and the nav entry it searches read as the same
 * thing. `barcode` and `shopping_cart` come from the section icon map in `NovaMainMenuBuilder`;
 * `receipt` and `description` from the leaf map in `TwnSingleMenuItem.utils.ts`.
 *
 * ⚠ These are copied, not imported — the leaf map is keyed by screaming-case label and the section
 * map lives in PHP, so neither can be read from here. A sidebar icon change must be repeated here.
 */
const SCOPE_ICON: Record<SearchScopeId, string> = {
  product: 'barcode',
  order: 'shopping_cart',
  invoice: 'receipt',
  bill: 'description'
}

/** One token per scope, so a scope's own in-flight request is cancelled and its siblings are not. */
const SEARCH_TOKEN: Record<SearchScopeId, string> = {
  product: 'command-center-product-search',
  order: 'command-center-order-search',
  invoice: 'command-center-invoice-search',
  bill: 'command-center-bill-search'
}

/** A row's trailing chip reads as a state, so a voided invoice must not wear the success colour. */
type BadgeTone = 'success' | 'warn' | 'danger' | 'neutral'

/**
 * The muted second line of a palette row — every variant that carries the matched code.
 *
 * One barcode can sit on several variants of one product, so this names all of them. A variant
 * with no options contributes nothing, which keeps a stray separator out of the line.
 *
 * @param matchedVariants - The variants that carry the matched SKU code
 * @returns The options of every named variant. Empty when none of them has options.
 */
const variantOptionsLine = (matchedVariants: MatchedVariant[]): string =>
  matchedVariants
    .map(variant => variant.variantOptions)
    .filter(Boolean)
    .join(', ')

/**
 * The barcodes a product row shows when the query did not name one — the first, and how many more.
 *
 * A name search matches no code, so the row would carry none. This reads every variant's codes
 * instead. A barcode shared by two variants counts once. A supplier reference is left out,
 * because it is no barcode — `SkuCodeTypeEnum::toBarcode()` returns null for it.
 *
 * @param product - The product with its variants and their SKU codes
 * @returns The first barcode and a count of the rest, such as `5901234123457 +2`. Empty when the
 * product holds no barcode.
 */
const productBarcodeLine = (product: IndexProduct): string => {
  const { formatSkuSummary } = useSkuDisplay()

  const barcodes = uniqBy(
    (product.variants ?? [])
      .flatMap(variant => variant.sku_codes ?? [])
      .filter(skuCode => skuCode.type !== SKU_FORMAT.SupRef),
    'code'
  )

  return formatSkuSummary(barcodes, { showType: false, prioritizeSupRef: false })
}

/**
 * The sub-line of a document row — who it is for, then when it was raised.
 *
 * Either half can be missing: a walk-in order has no customer, and a draft bill may carry no
 * date. Joining only what is present keeps a lone separator off the row.
 */
const documentSubLabel = (party: string | null | undefined, date: string | null | undefined): string | undefined =>
  [party, date ? formatRowDate(date) : null].filter(Boolean).join(' · ') || undefined

/**
 * A palette row shows the day, never the minute. `toLocaleDateString` rather than the app's date
 * utilities, because a row is one line of context and a full timestamp would crowd out the name
 * beside it.
 */
const formatRowDate = (value: string): string =>
  new Date(value).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })

/**
 * The colour a state earns. Every backend state this palette can show is named here, and anything
 * unnamed reads neutral — a new state must never be able to arrive wearing green.
 */
const STATUS_TONE: Record<string, BadgeTone> = {
  PAID: 'success',
  BILLED: 'success',
  SENT: 'success',
  PARTIALLY_PAID: 'warn',
  PARTIALLY_REFUNDED: 'warn',
  OVER_DUE: 'danger',
  PAYMENT_ISSUE: 'danger',
  NOT_PAID: 'danger',
  VOID: 'danger',
  CANCELLED: 'danger',
  DRAFT: 'neutral'
}

const toneForStatus = (status: string): BadgeTone => STATUS_TONE[status] ?? 'neutral'

/**
 * The unit catalogue, for a badge that reads a stock figure. The palette has no component to hold the query, so it
 * reads the shared cache. A catalogue that cannot load leaves the figure bare rather than failing the search.
 */
const loadUnits = async () => {
  try {
    return await queryClient.ensureQueryData(measurementUnitsQueryOptions)
  } catch {
    return []
  }
}

const searchProducts = async (searchTerm: string): Promise<SearchResultCommand[]> => {
  const { findMatchingVariants } = useSkuMatcher()
  const { getVariantOptions } = useProductVariantHelper()

  // Both reads start together, so a cold unit catalogue does not add a second round trip to the first search.
  const [response, units] = await Promise.all([
    callProducts().index({
      queryParams: { q: searchTerm, with: 'variants.skuCodes', per_page: ROWS_PER_SCOPE },
      preventConcurrent: true,
      token: SEARCH_TOKEN.product
    }),
    loadUnits()
  ])

  return (response.data.value?.data ?? []).map(item => {
    const matches = findMatchingVariants(searchTerm, item)
    const stock = stockSummary(item)

    const matchedVariants: MatchedVariant[] = matches.map(match => ({
      variantOptions: getVariantOptions(match.matchedVariant) ?? ''
    }))

    // Product title and variant options ride along for the duplicate-SKU dialog.
    const skuMatch: SkuMatchInfo = matches.length
      ? {
          hasMatch: true,
          matchedSku: matches[0].matchedSku,
          productTitle: item.title,
          matchedVariants
        }
      : { hasMatch: false }

    return {
      id: `search-result-product-${item.id}`.toLowerCase(),
      type: 'searchResult',
      resultType: 'product',
      label: item.title,
      subLabel: variantOptionsLine(matchedVariants) || undefined,
      // A trailing chip, not baked into the label. The matched code when the query named one —
      // every match shares it, so the first names the chip — and the product's own barcodes when
      // it did not, so a name search still shows them.
      badge: skuMatch.matchedSku ?? (productBarcodeLine(item) || undefined),
      badgeTone: skuMatch.hasMatch ? 'success' : 'neutral',
      icon: SCOPE_ICON.product,
      originalData: item,
      skuMatch,
      stockBadge: stockLevelBadge(stock, stockUnitOfRelation(units, stock.rows[0]?.pricingUnit)),
      isPack: isPack(item),
      execute: item.slug ? () => router.get(route('admin.products.edit', { product: item.slug })) : undefined,
      onSuccess: 'closeMenu' as const
    }
  })
}

/**
 * An order is named by its `order_no` when the tenant issues one, and by its running number
 * otherwise — `allow_custom_order_no` is a per-tenant setting, so half our tenants have no prefix
 * at all and `#412` is the only name their staff would recognise.
 */
const orderLabel = (order: IndexOrder): string => order.order_no ?? `#${order.auto_increment}`

/**
 * Who the row is for. The company first — a trade order is recognised by its account, not by
 * whoever rang it up — then the customer, then the till user. A walk-in cash sale has none.
 */
const orderParty = (order: IndexOrder): string | null =>
  order.company?.name ?? personName(order.customer) ?? personName(order.placed_by) ?? null

/** `full_name` when the backend composed one, the halves joined when it did not. */
const personName = (person: User | LoyaltyCustomer | null | undefined): string | null => {
  if (!person) return null

  const composed = 'full_name' in person ? person.full_name : null

  return composed || [person.first_name, person.last_name].filter(Boolean).join(' ') || null
}

const searchOrders = async (searchTerm: string): Promise<SearchResultCommand[]> => {
  const response = await callOrders().index({
    queryParams: { q: searchTerm, per_page: ROWS_PER_SCOPE },
    preventConcurrent: true,
    token: SEARCH_TOKEN.order
  })

  return (response.data.value?.data ?? []).map(order => ({
    id: `search-result-order-${order.id}`.toLowerCase(),
    type: 'searchResult',
    resultType: 'order',
    label: orderLabel(order),
    subLabel: documentSubLabel(orderParty(order), order.created_at),
    badge: toTitleCase(order.payment_status),
    badgeTone: toneForStatus(order.payment_status),
    icon: SCOPE_ICON.order,
    originalData: order,
    // The route is `orders/{orderId}`, not `{order}` — a wrong key lands on the listing with a
    // query string rather than the order.
    execute: () => router.get(route('admin.orders.show', { orderId: order.id })),
    onSuccess: 'closeMenu' as const
  }))
}

/** A draft invoice has no `invoice_number` and shows its draft number instead — so does its row. */
const invoiceLabel = (invoice: Invoice): string => invoice.invoice_number ?? invoice.draft_number

const searchInvoices = async (searchTerm: string): Promise<SearchResultCommand[]> => {
  const response = await callInvoices().index({
    queryParams: { q: searchTerm, per_page: ROWS_PER_SCOPE },
    preventConcurrent: true,
    token: SEARCH_TOKEN.invoice
  })

  return (response.data.value?.data ?? []).map(invoice => ({
    id: `search-result-invoice-${invoice.id}`.toLowerCase(),
    type: 'searchResult',
    resultType: 'invoice',
    label: invoiceLabel(invoice),
    subLabel: documentSubLabel(personName(invoice.order?.customer), invoice.invoice_date),
    badge: toTitleCase(invoice.status),
    badgeTone: toneForStatus(invoice.status),
    icon: SCOPE_ICON.invoice,
    originalData: invoice,
    execute: () => router.get(route('admin.invoices.show', { invoice: invoice.id })),
    onSuccess: 'closeMenu' as const
  }))
}

const searchBills = async (searchTerm: string): Promise<SearchResultCommand[]> => {
  const response = await callBills().index({
    queryParams: { q: searchTerm, per_page: ROWS_PER_SCOPE },
    preventConcurrent: true,
    token: SEARCH_TOKEN.bill
  })

  return (response.data.value?.data ?? []).map((bill: IndexBill) => ({
    id: `search-result-bill-${bill.id}`.toLowerCase(),
    type: 'searchResult',
    resultType: 'bill',
    label: bill.bill_number,
    subLabel: documentSubLabel(bill.supplier?.name ?? null, bill.bill_date),
    badge: toTitleCase(bill.status),
    badgeTone: toneForStatus(bill.status),
    icon: SCOPE_ICON.bill,
    originalData: bill,
    execute: () => router.get(route('admin.finance.bills.show', { bill: bill.id })),
    onSuccess: 'closeMenu' as const
  }))
}

/**
 * The four scopes, in the order their pills and their result groups appear.
 *
 * Adding a fifth is one entry here. Nothing else in the Command Center enumerates scopes.
 */
const SEARCH_SCOPES: SearchScope[] = [
  {
    id: 'product',
    label: 'Products',
    icon: SCOPE_ICON.product,
    permission: { action: 'view', subject: 'catalog' },
    isDefault: true,
    search: searchProducts
  },
  {
    id: 'order',
    label: 'Orders',
    icon: SCOPE_ICON.order,
    permission: { action: 'view', subject: 'orders' },
    isDefault: true,
    search: searchOrders
  },
  {
    // Shares `finance.view` with bills, so the two pills arrive and leave together. Splitting
    // them needs two new backend permissions; see COR-1645.
    id: 'invoice',
    label: 'Invoices',
    icon: SCOPE_ICON.invoice,
    permission: { action: 'view', subject: 'finance' },
    isDefault: false,
    search: searchInvoices
  },
  {
    id: 'bill',
    label: 'Bills',
    icon: SCOPE_ICON.bill,
    permission: { action: 'view', subject: 'finance' },
    isDefault: false,
    search: searchBills
  }
]

export type { BadgeTone }

export {
  SEARCH_SCOPES,
  // Exported for testing
  variantOptionsLine,
  productBarcodeLine,
  documentSubLabel,
  toneForStatus,
  orderLabel,
  invoiceLabel,
  personName,
  orderParty
}
